<template>
  <div class="bg-white shadow-sm border border-slate-200 overflow-hidden">
    <div class="px-6 pt-2 pb-1">
      <div class="flex items-baseline gap-2 flex-wrap">
        <h2 class="text-base font-bold text-[#0d4169] text-left">
          {{ resolvedCompanyName }}
        </h2>
        <span class="text-sm font-bold text-slate-600">V.A.T Report</span>
      </div>
    </div>

    <div class="px-6 pb-6 pt-2">
      <div class="w-full max-lg:overflow-x-auto">
        <div class="flex flex-wrap items-center gap-4 pb-2">
  <!-- Left -->
  <h3 class="w-[150px] text-[0.78rem] font-semibold text-[#1e293b] m-0 shrink-0">
    <!-- V.A.T Report -->
  </h3>

  <!-- Right -->
  <div
    class="flex flex-wrap sm:flex-nowrap items-center justify-between gap-4 sm:gap-8 w-full sm:flex-1"
  >
    <!-- Select Month -->
    <div class="flex items-center gap-2 shrink-0">
      <label
        for="vat-month-select"
        class="text-[0.78rem] font-semibold text-[#1e293b] whitespace-nowrap"
      >
        Select Month
      </label>

      <div class="relative min-w-[320px] vat-month-select-wrap">
        <select
          id="vat-month-select"
          v-model="selectedMonth"
          class="absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0"
          @change="onMonthSelected"
        >
          <option value="">Select Month</option>

          <option
            v-for="month in monthOptions"
            :key="month.value"
            :value="month.value"
          >
            {{ month.label }}
          </option>
        </select>

        <div
          class="vat-month-select-display pointer-events-none truncate rounded-md border border-[#cbd5e1] bg-white px-2.5 py-1.5 text-left text-sm transition-all"
          :class="selectedMonth ? 'text-[#1e293b]' : 'text-slate-400'"
        >
          {{ selectedMonthDisplay }}
        </div>
      </div>
    </div>

    <!-- VAT -->
    <div class="flex items-baseline gap-1.5 whitespace-nowrap shrink-0">
      <span class="text-[0.78rem] font-bold text-[#0d4169]">
        V.A.T :
      </span>

      <span
        v-if="isLoadingVat"
        class="inline-block h-3.5 w-3.5 border-2 border-[#0d4169]/40 border-t-[#0d4169] rounded-full animate-spin"
      ></span>

      <span
        v-else
        class="text-sm font-bold text-[#0d4169] min-w-[3rem]"
      >
        {{ formatNetVat(netVat) }}
      </span>
    </div>

    <!-- Unsaved VAT -->
    <div class="flex items-baseline gap-1.5 whitespace-nowrap shrink-0">
      <span class="text-[0.78rem] font-bold text-red-600">
        Unsaved V.A.T :
      </span>

      <span
        v-if="isLoadingUnsavedVat"
        class="inline-block h-3.5 w-3.5 border-2 border-red-400/40 border-t-red-600 rounded-full animate-spin"
      ></span>

      <span
        v-else
        class="text-sm font-bold text-red-600 min-w-[3rem]"
      >
        {{ formatUnsavedVat(unsavedVat) }}
      </span>
    </div>
  </div>
</div>
        <table class="vat-table w-full max-lg:min-w-[640px] border-collapse bg-white rounded-xl overflow-hidden shadow-sm">
          <thead>
            <tr>
              <th class="col-line">Line Number</th>
              <th class="col-desc">Line Description</th>
              <th class="num">Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in vatRows"
              :key="row.lineNumber"
              class="hover:bg-slate-100/50 transition-colors border-b"
            >
              <td class="col-line">{{ row.lineNumber }}</td>
              <td class="col-desc">{{ row.description }}</td>
              <td class="num">{{ formatAmount(row.amount) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useStore } from 'vuex'
import { apiFetch } from '../../../utils/api'

const store = useStore()
const selectedMonth = ref('')
const netVat = ref(null)
const unsavedVat = ref(null)
const isLoadingVat = ref(false)
const isLoadingUnsavedVat = ref(false)
let vatRequestId = 0

/** Ethiopian calendar epoch (JDN). */
const ETHIOPIAN_EPOCH = 1723856

/**
 * English month labels mapped to Ethiopian months by common business usage.
 * Example: July -> Hamle (month 11) -> Gregorian Jul 8 to Aug 6.
 */
const monthOptions = [
  { value: '1', label: 'January', ethiopianMonth: 5, ethiopianName: 'Tir' },
  { value: '2', label: 'February', ethiopianMonth: 6, ethiopianName: 'Yekatit' },
  { value: '3', label: 'March', ethiopianMonth: 7, ethiopianName: 'Megabit' },
  { value: '4', label: 'April', ethiopianMonth: 8, ethiopianName: 'Miyaziya' },
  { value: '5', label: 'May', ethiopianMonth: 9, ethiopianName: 'Ginbot' },
  { value: '6', label: 'June', ethiopianMonth: 10, ethiopianName: 'Sene' },
  { value: '7', label: 'July', ethiopianMonth: 11, ethiopianName: 'Hamle' },
  { value: '8', label: 'August', ethiopianMonth: 12, ethiopianName: 'Nehase' },
  { value: '9', label: 'September', ethiopianMonth: 1, ethiopianName: 'Meskerem' },
  { value: '10', label: 'October', ethiopianMonth: 2, ethiopianName: 'Tikimt' },
  { value: '11', label: 'November', ethiopianMonth: 3, ethiopianName: 'Hidar' },
  { value: '12', label: 'December', ethiopianMonth: 4, ethiopianName: 'Tahsas' }
]

function gregorianToJDN(year, month, day) {
  const a = Math.floor((14 - month) / 12)
  const y = year + 4800 - a
  const m = month + 12 * a - 3
  return (
    day +
    Math.floor((153 * m + 2) / 5) +
    365 * y +
    Math.floor(y / 4) -
    Math.floor(y / 100) +
    Math.floor(y / 400) -
    32045
  )
}

function jdnToGregorian(jdn) {
  let r = jdn + 68569
  const n = Math.floor((4 * r) / 146097)
  r -= Math.floor((146097 * n + 3) / 4)
  const y = Math.floor((4000 * (r + 1)) / 1461001)
  r = r - Math.floor((1461 * y) / 4) + 31
  const m = Math.floor((80 * r) / 2447)
  const d = r - Math.floor((2447 * m) / 80)
  r = Math.floor(m / 11)
  const month = m + 2 - 12 * r
  const year = 100 * (n - 49) + y + r
  return { year, month, day: d }
}

function ethiopianToJDN(year, month, day) {
  return ETHIOPIAN_EPOCH - 1 + 365 * (year - 1) + Math.floor((year - 1) / 4) + 30 * (month - 1) + day
}

function ethiopianToGregorian(year, month, day) {
  return jdnToGregorian(ethiopianToJDN(year, month, day))
}

function gregorianToEthiopian(year, month, day) {
  const jdn = gregorianToJDN(year, month, day)
  const r = jdn - ETHIOPIAN_EPOCH
  const ethYear = Math.floor((4 * r + 3) / 1461)
  const ethMonth =
    Math.floor((r - (365 * (ethYear - 1) + Math.floor((ethYear - 1) / 4))) / 30) + 1
  const ethDay =
    r - (365 * (ethYear - 1) + Math.floor((ethYear - 1) / 4) + 30 * (ethMonth - 1)) + 1
  return { year: ethYear, month: ethMonth, day: ethDay }
}

function toISODate({ year, month, day }) {
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

/**
 * Resolve the Ethiopian year for a selected English month relative to today.
 * Future months in the current Gregorian year use the previous year's period
 * (e.g. September when today is July 2026 -> Sep 2025).
 */
function resolveEthiopianYearForSelectedMonth(monthValue, referenceDate = new Date()) {
  const option = monthOptions.find((month) => month.value === monthValue)
  if (!option) return null

  const currentGregorianMonth = referenceDate.getMonth() + 1
  const selectedGregorianMonth = parseInt(monthValue, 10)
  let targetGregorianYear = referenceDate.getFullYear()
  if (selectedGregorianMonth > currentGregorianMonth) {
    targetGregorianYear -= 1
  }

  const baseEthYear = gregorianToEthiopian(
    referenceDate.getFullYear(),
    referenceDate.getMonth() + 1,
    referenceDate.getDate()
  ).year

  let bestEthYear = baseEthYear
  let bestScore = -1

  for (let ethYear = baseEthYear - 1; ethYear <= baseEthYear + 2; ethYear++) {
    const fromGregorian = ethiopianToGregorian(ethYear, option.ethiopianMonth, 1)
    const toGregorian = ethiopianToGregorian(ethYear, option.ethiopianMonth, 30)
    let score = 0
    if (fromGregorian.year === targetGregorianYear) score += 2
    if (toGregorian.year === targetGregorianYear) score += 1
    if (score > bestScore) {
      bestScore = score
      bestEthYear = ethYear
    }
  }

  return bestEthYear
}

function getGregorianRangeForSelectedMonth(monthValue) {
  const option = monthOptions.find((month) => month.value === monthValue)
  if (!option) return null

  const ethYear = resolveEthiopianYearForSelectedMonth(monthValue)
  const fromGregorian = ethiopianToGregorian(ethYear, option.ethiopianMonth, 1)
  const toGregorian = ethiopianToGregorian(ethYear, option.ethiopianMonth, 30)

  return {
    option,
    ethYear,
    fromDate: toISODate(fromGregorian),
    toDate: toISODate(toGregorian)
  }
}

function formatMonthOptionLabel(month) {
  const range = getGregorianRangeForSelectedMonth(month.value)
  if (!range) return month.label
  return `${month.label}(${range.fromDate} to ${range.toDate})`
}

const selectedMonthDisplay = computed(() => {
  if (!selectedMonth.value) return 'Select Month'
  const month = monthOptions.find((item) => item.value === selectedMonth.value)
  if (!month) return 'Select Month'
  return formatMonthOptionLabel(month)
})

function formatNetVat(value) {
  if (value === null || value === undefined || value === '') return ''
  const n = Number(value)
  if (!Number.isFinite(n)) return ''
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function formatUnsavedVat(value) {
  if (value === null || value === undefined || value === '') return ''
  const n = Number(value)
  if (!Number.isFinite(n)) return '0.00'
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

async function fetchUnsavedVat() {
  const res = await apiFetch('/finance/vat-report/unsaved-vat')
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err.message || 'Failed to load unsaved VAT.')
  }
  return res.json()
}

async function loadUnsavedVat() {
  isLoadingUnsavedVat.value = true
  try {
    const data = await fetchUnsavedVat()
    unsavedVat.value = data?.unsavedVat ?? 0
  } catch {
    unsavedVat.value = 0
  } finally {
    isLoadingUnsavedVat.value = false
  }
}

async function fetchVatSummary(fromDate, toDate) {
  const qs = new URLSearchParams({ fromDate, toDate })
  const res = await apiFetch(`/finance/vat-report?${qs.toString()}`)
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err.message || 'Failed to load VAT summary.')
  }
  return res.json()
}

async function onMonthSelected() {
  if (!selectedMonth.value) {
    netVat.value = null
    return
  }

  const range = getGregorianRangeForSelectedMonth(selectedMonth.value)
  if (!range) return

  const requestId = ++vatRequestId
  isLoadingVat.value = true
  netVat.value = null

  try {
    const data = await fetchVatSummary(range.fromDate, range.toDate)
    if (requestId !== vatRequestId) return
    netVat.value = data?.netVat ?? 0
  } catch {
    if (requestId !== vatRequestId) return
    netVat.value = null
  } finally {
    if (requestId === vatRequestId) {
      isLoadingVat.value = false
    }
  }
}

const resolvedCompanyName = computed(() => {
  const user = store.getters.currentUser || store.state.user || {}
  return (
    user?.company?.name ||
    user?.companyName ||
    user?.company_name ||
    user?.company?.company_name ||
    'Company Name'
  )
})

onMounted(() => {
  loadUnsavedVat()
})

const vatRows = [
  { lineNumber: 5, description: 'TAXABLE SALES/ SUPPLIES', amount: null },
  { lineNumber: 10, description: 'VAT ON TAXABLE SALES/ SUPPLIES', amount: 0 },
  { lineNumber: 15, description: 'ZERO-RATED SALES/ SUPPLIES', amount: null },
  { lineNumber: 20, description: 'TAX-EXEMPT SALES/ SUPPLIES', amount: null },
  { lineNumber: 25, description: 'SERVICES OR SUPPLIES SUBJECT TO REVERSE TAXATION', amount: null },
  { lineNumber: 30, description: 'VAT ON SERVICES OR SUPPLIES SUBJECT TO REVERSE TAXATION', amount: 0 },
  { lineNumber: 35, description: 'TAX ADJUSTMENT WITH DEBIT NOTE', amount: null },
  { lineNumber: 40, description: 'VAT ON TAX ADJUSTMENT WITH DEBIT NOTE', amount: 0 },
  { lineNumber: 45, description: 'TAX ADJUSTMENT WITH CREDIT NOTE', amount: null },
  { lineNumber: 50, description: 'VAT ON TAX ADJUSTMENT WITH CREDIT NOTE', amount: 0 },
  { lineNumber: 55, description: 'TOTAL SALES/ SUPPLIES (5+15+20+25+35)-45)', amount: 0 },
  { lineNumber: 60, description: 'VAT ON TOTAL SALES/ SUPPLIES(10+30+40)-50)', amount: 0 },
  { lineNumber: 65, description: 'LOCAL PURCHASE CAPITAL ASSETS', amount: null },
  { lineNumber: 70, description: 'VAT ON LOCAL PURCHASE CAPITAL ASSETS', amount: 0 },
  { lineNumber: 75, description: 'IMPORTED CAPITAL ASSETS', amount: null },
  { lineNumber: 80, description: 'VAT ON IMPORTED CAPITAL ASSETS', amount: 0 },
  { lineNumber: 85, description: 'PURCHASE WITH NO VAT OR UNCLAIMED INPUTS', amount: null },
  { lineNumber: 90, description: 'TOTAL CAPITAL ASSETS (65+75+85)', amount: 0 },
  { lineNumber: 95, description: 'VAT ON TOTAL ASSETS', amount: 0 },
  { lineNumber: 100, description: 'LOCAL PURCHASE INPUTS', amount: null },
  { lineNumber: 105, description: 'VAT ON LOCAL PURCHASE INPUTS', amount: 0 },
  { lineNumber: 110, description: 'IMPORTED INPUTS', amount: null },
  { lineNumber: 115, description: 'VAT ON IMPORTED INPUTS', amount: 0 },
  { lineNumber: 120, description: 'GENERAL EXPENSE INPUTS PURCHASE', amount: null },
  { lineNumber: 125, description: 'VAT ON GENERAL EXPENSE INPUTS', amount: 0 },
  { lineNumber: 130, description: 'PURCHASE WITH NO VAT OR UNCLAIMED INPUTS', amount: null },
  { lineNumber: 135, description: 'DEDUCTIBLE ON VAT REVERSE TAXATION', amount: null },
  { lineNumber: 140, description: 'VAT ON DEDUCTIBLE ON VAT REVERSE TAXATION', amount: 0 },
  { lineNumber: 145, description: 'TAX ADJUSTMENTS WITH DEBIT NOTE', amount: null },
  { lineNumber: 150, description: 'VAT ON TAX ADJUSTMENTS WITH DEBIT NOTE', amount: 0 },
  { lineNumber: 155, description: 'TAX ADJUSTMENTS WITH CREDIT NOTE', amount: null },
  { lineNumber: 160, description: 'VAT ON TAX ADJUSTMENTS WITH CREDIT NOTE', amount: 0 },
  { lineNumber: 165, description: 'TOTAL NON CAPITAL INPUTS ((100+110+120+130+135+145)-155)', amount: 0 },
  { lineNumber: 170, description: 'VAT ON TOTAL INPUTS (105+115+125+140+150)-160', amount: 0 },
  { lineNumber: 175, description: 'VAT ON GOVERNMENT VOUCHER', amount: null },
  { lineNumber: 180, description: 'OTHER CREDITS FOR THE MONTH(PAYMENTS, SALES TAX,GOODS ON HAND)', amount: null },
  { lineNumber: 185, description: 'VAT DUE FOR THE MONTH (BEFORE PREVIOUS MONTH CREDIT)', amount: 0 },
  { lineNumber: 190, description: 'VAT CREDIT FOR THE MONTH (BEFORE PREVIOUS MONTH CREDIT)', amount: 0 },
  { lineNumber: 195, description: 'CREDIT CARRIED FORWARD FROM PREVIOUS MONTH', amount: null },
  { lineNumber: 196, description: 'Refund Given for Excess Credit', amount: null },
  { lineNumber: 197, description: 'Refund Interest Adjustment', amount: null },
  { lineNumber: 200, description: 'AMOUNT TO BE PAID', amount: 0 },
  { lineNumber: 205, description: 'VAT CREDIT AVAILABLE FOR CREDIT CARRY FORWARD', amount: 0 }
]

function formatAmount(value) {
  if (value === null || value === undefined || value === '') return ''
  return value
}
</script>

<style scoped>
.vat-table {
  font-family: 'Segoe UI', sans-serif;
  font-size: 0.8rem;
  color: #1e293b;
}

.vat-table th,
.vat-table td {
  padding: 6px 8px;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
  font-size: 0.7rem;
  vertical-align: middle;
}

.vat-table th {
  background: #f1f5f9;
  font-weight: 600;
}

.vat-table tbody tr:hover {
  background: #f1f5f9;
}

.vat-table .col-line {
  width: 6.5rem;
  min-width: 6.5rem;
  white-space: nowrap;
  text-align: center;
}

.vat-table .col-desc {
  white-space: normal;
  word-break: break-word;
  color: #334155;
}

.vat-table th.num,
.vat-table td.num {
  width: 7rem;
  min-width: 7rem;
  text-align: right;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.vat-month-select-wrap:focus-within .vat-month-select-display {
  border-color: #0d4169;
}
</style>
